'use client';

import React, { useCallback } from 'react';

/**
 * SkipLink — keyboard accessibility skip navigation component.
 *
 * Activating this link moves programmatic focus to #main-content.
 * The <main> element must have tabIndex={-1} so it can receive focus
 * without entering the normal Tab sequence.
 *
 * Native fragment navigation in Next.js (client-side router) does not
 * reliably move focus to the target element, so we call .focus()
 * explicitly after the hash change.
 */
export default function SkipLink() {
  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const main = document.getElementById('main-content');
    if (main) {
      // Ensure the element is programmatically focusable
      if (!main.hasAttribute('tabindex')) {
        main.setAttribute('tabindex', '-1');
      }
      main.focus();
      // Update the URL hash without triggering a scroll jump
      window.history.replaceState(null, '', '#main-content');
    }
  }, []);

  return (
    <a href="#main-content" className="skip-to-main" onClick={handleClick}>
      Skip to main content
    </a>
  );
}
